import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  loadSchemaExchangeRates,
  loadOfferFees,
  updateOfferFeeCatalog,
  changeEditableProperty,
  cancelFeesChanges,
  getOfferFeeCatalogNegotiationData,
  startNegotiation,
  updateOfferFeeApproval1,
  updateOfferFeeApproval2,
  sendNegotiationForApproval,
  confirmNegotiation,
  confirmPartnerNegotiation,
  confirmPartnerGroupNegotiation
} from '../actionCreators';
import OfferFeeCatalogCard from './OfferFeeCatalogCard';
import { NegotiationType } from '../../../../../../constants/incomeSchema';

export const OfferFeeCatalogContainer = ({ documentId, siteId, numberOfFees }) => {
  const dispatch = useDispatch();

  const [isDirty, setIsDirty] = useState(false);
  const [localFees, setLocalFees] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const fees = useSelector(state => state.currentOffer.feesData.fees, shallowEqual);
  const feesLoaded = useSelector(state => state.currentOffer.feesData.feesLoaded, shallowEqual);
  const feeCatalogLoaded = useSelector(state => state.currentOffer.feesData.feeCatalogLoaded, shallowEqual);
  const saving = useSelector(state => state.currentOffer.feesData.saving, shallowEqual);
  const editable = useSelector(state => state.currentOffer.feesData.editable, shallowEqual);
  const schemaIsEligibleForNegotiation = useSelector(state => state.currentOffer.feesData.schemaIsEligibleForNegotiation, shallowEqual);
  const negotiationStatus = useSelector(state => state.currentOffer.feesData.negotiationStatus, shallowEqual);
  const negotiationType = useSelector(state => state.currentOffer.feesData.negotiationType, shallowEqual);
  const exchangeRates = useSelector(state => state.currentOffer.feesData.schemaExchangeRates, shallowEqual);

  useEffect(() => {
    if (!isExpanded) return;
    dispatch(loadSchemaExchangeRates(documentId, siteId));
  }, [dispatch, documentId, siteId, isExpanded]);

  useEffect(() => {
    if (feesLoaded || !isExpanded) return;
    dispatch(loadOfferFees(documentId, siteId));
  }, [dispatch, documentId, feesLoaded, siteId, isExpanded]);

  useEffect(() => {
    if (!feesLoaded) return;
    setLocalFees(fees);
  }, [documentId, fees, feesLoaded, siteId]);

  useEffect(() => {
    if (feeCatalogLoaded || !isExpanded) return;
    dispatch(getOfferFeeCatalogNegotiationData(documentId, siteId));
  }, [dispatch, documentId, feeCatalogLoaded, siteId, isExpanded]);

  useEffect(() => {
    return () => {
      dispatch(cancelFeesChanges());
    };
  }, [dispatch]);

  const handlePropertyChange = (index, propName, value) => {
    setLocalFees(Object.assign([...localFees], { [index]: { ...localFees[index], [propName]: value } }));
    setIsDirty(true);
  };

  const handleModifyData = () => {
    dispatch(changeEditableProperty(true));
  };

  const onApprovalButtonClick = (index, propName) => {
    switch (propName) {
      case 'yes_1':
        dispatch(updateOfferFeeApproval1(documentId, siteId, localFees[index].feeId, true));
        break;
      case 'yes_2':
        dispatch(updateOfferFeeApproval2(documentId, siteId, localFees[index].feeId, true));
        break;
      case 'no_1':
        dispatch(updateOfferFeeApproval1(documentId, siteId, localFees[index].feeId, false));
        break;
      case 'no_2':
        dispatch(updateOfferFeeApproval2(documentId, siteId, localFees[index].feeId, false));
        break;
      default:
        return;
    }
  };

  const onSendNegotiationForApprovalButtonClick = () => {
    let negotiatedFees = [];
    localFees.forEach(fee => {
      if (fee.approvalStatus && fee.approvalStatus !== '') {
        negotiatedFees.push({
          feeId: fee.feeId,
          percentProposed: fee.percentProposed,
          valueProposed: fee.valueProposed,
          incomeTypeId: fee.incomeTypeId,
          feeName: fee.name
        });
      }
    });
    dispatch(sendNegotiationForApproval(documentId, siteId, negotiatedFees));
  };

  const onConfirmNegotiationButtonClick = () => {
    switch (negotiationType) {
      case NegotiationType.DOCUMENT:
        dispatch(confirmNegotiation(documentId, siteId));
        break;
      case NegotiationType.PARTNER:
        dispatch(confirmPartnerNegotiation(documentId, siteId));
        break;
      case NegotiationType.PARTNER_GROUP:
        dispatch(confirmPartnerGroupNegotiation(documentId, siteId));
        break;
      default:
        return;
    }
  };

  const onStartNegotiationButtonClick = negotiationType => {
    switch (negotiationType) {
      case 'document':
        dispatch(startNegotiation(documentId, siteId, NegotiationType.DOCUMENT));
        break;
      case 'partner':
        dispatch(startNegotiation(documentId, siteId, NegotiationType.PARTNER));
        break;
      case 'partner-group':
        dispatch(startNegotiation(documentId, siteId, NegotiationType.PARTNER_GROUP));
        break;
      default:
        return;
    }
  };

  const onExpand = (_, expanded) => {
    setIsExpanded(!expanded);
  };

  const handleSave = () => {
    dispatch(changeEditableProperty(false));

    let modifiesFees = [];
    localFees.forEach(fee => {
      if (!(fee.approvalStatus && fee.approvalStatus !== ''))
        modifiesFees.push({
          feeId: fee.feeId,
          percent: fee.percent,
          value: fee.value,
          minimumValue: fee.minimumValue,
          currencyId: fee.currencyId
        });
    });

    dispatch(updateOfferFeeCatalog(documentId, siteId, modifiesFees));
  };

  const onPropertyChange = (idx, prop, value) => {
    handlePropertyChange(idx, prop, value);
  };

  return (
    <OfferFeeCatalogCard
      fees={localFees}
      numberOfFees={numberOfFees}
      schemaIsEligibleForNegotiation={schemaIsEligibleForNegotiation}
      onPropertyChange={onPropertyChange}
      onSave={handleSave}
      saving={saving}
      editable={editable}
      handleModifyData={handleModifyData}
      onApprovalButtonClick={onApprovalButtonClick}
      onStartNegotiationButtonClick={onStartNegotiationButtonClick}
      onConfirmNegotiationButtonClick={onConfirmNegotiationButtonClick}
      onSendNegotiationForApprovalButtonClick={onSendNegotiationForApprovalButtonClick}
      negotiationStatus={negotiationStatus}
      isDirty={isDirty}
      loaded={feesLoaded && feeCatalogLoaded}
      exchangeRates={exchangeRates || []}
      onExpand={onExpand}
      isExpanded={isExpanded}
    />
  );
};

OfferFeeCatalogContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  numberOfFees: PropTypes.number
};

export default OfferFeeCatalogContainer;
